import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { HardHat } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { ApiError } from './lib/api.js';
import { AuthProvider, useAuth } from './lib/auth.js';
import { router } from './router.js';
import { ForcedPasswordChangePage } from './routes/PublicAuthPages.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Erro de permissão ou de validação não melhora com repetição.
        if (error instanceof ApiError && error.status && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/** Tela de espera enquanto a sessão inicial é resolvida pelo refresh token. */
function BootScreen() {
  return (
    <div className="grid min-h-dvh place-items-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <span className="grid size-12 place-items-center rounded-xl bg-primary">
          <HardHat size={24} strokeWidth={1.5} className="text-primary-foreground" aria-hidden />
        </span>
        <p className="text-[13px] text-muted-foreground">Carregando…</p>
      </div>
    </div>
  );
}

function RoutedApp() {
  const { user, loading, inPlatform, mustChangePassword } = useAuth();
  const isAuthenticated = Boolean(user);

  const context = useMemo(() => ({ isAuthenticated, inPlatform }), [isAuthenticated, inPlatform]);

  /*
   * Sessão perdida (ou recém-obtida) muda o resultado dos guardas de rota.
   * `invalidate` reavalia a rota atual e aplica o redirecionamento.
   */
  useEffect(() => {
    if (!loading) void router.invalidate();
  }, [isAuthenticated, inPlatform, loading]);

  if (loading) return <BootScreen />;

  /*
   * Senha padrão ainda de pé: a troca toma a tela inteira, no lugar do roteador.
   *
   * Trocar por um guarda de rota deixaria a casca montada e cada página
   * disparando consultas que o servidor recusa — a pessoa veria o painel
   * piscando erro atrás do formulário. Aqui não há rota nenhuma a alcançar,
   * que é exatamente o que a API está dizendo.
   */
  if (isAuthenticated && mustChangePassword) return <ForcedPasswordChangePage />;

  return <RouterProvider router={router} context={context} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RoutedApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}
